"use client";

import { useEffect, useState, useCallback } from "react";
import { useTranslations } from "next-intl";
import { format } from "date-fns";
import type { Locale } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import {
  Drawer,
  DrawerNestedRoot,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { DateRange } from "react-day-picker";
import React from "react";

interface BookingDatePickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "daily" | "timeslot";
  pickupDate?: Date;
  returnDate?: Date;
  onApply: (pickupDate: Date, returnDate?: Date) => void;
  onReset?: () => void;
  disabledDate?: (date: Date) => boolean;
  locale?: Locale;
  variant?: "drawer" | "dialog";
  /** true이면 일반 Drawer + noBodyStyles 사용 (커스텀 드로어 위에서 사용 시) */
  standalone?: boolean;
  /**
   * 오늘 · 내일 · 이번 주말 바로가기 카드(09-23, 에어비앤비 방식) — 검색 날짜 선택에서만
   * 켠다. 누르면 그 기간이 임시 선택에 들어가고(달력에 표시), "적용"으로 확정한다.
   */
  quickPicks?: boolean;
}

// Shared calendar classNames for Yanolja-style UI
const calendarClassName = cn(
  // Override rdp CSS variables for day cell & button sizing
  "[--rdp-day-height:44px] [--rdp-day-width:auto] [--rdp-day_button-width:36px] [--rdp-day_button-height:36px] [--rdp-day_button-border-radius:8px] [--rdp-day_button-border:none]",
  "[--cell-size:--spacing(9)]",
  // Weekday header — default gray, Sunday(1st) red, Saturday(last) blue
  "[&_.rdp-weekday]:text-sm [&_.rdp-weekday]:text-gray-500 [&_.rdp-weekday]:font-medium",
  "[&_thead_tr>th:first-child]:text-red-500!",
  "[&_thead_tr>th:last-child]:text-blue-500!",
  // Week row spacing
  "[&_.rdp-week]:mt-1",
);

// Custom Day component: wraps children in a div for range band styling
// The inner div gets the range background instead of the td itself
function BookingDay(
  props: {
    day: { date: Date };
    modifiers: Record<string, boolean>;
  } & React.HTMLAttributes<HTMLDivElement>,
) {
  const { day, modifiers, className, children, ...tdProps } = props;
  const isRangeStart = modifiers.range_start;
  const isRangeEnd = modifiers.range_end;
  const isRangeMiddle = modifiers.range_middle;
  const isSelected = isRangeStart || isRangeEnd || isRangeMiddle;
  const isDisabled = modifiers.disabled;
  const dayOfWeek = day.date.getDay(); // 0=Sun, 6=Sat

  // For start: gray band only on right half. For end: only left half.
  // For middle: full width. For start+end (same day): no band.
  const showBand =
    (isRangeStart || isRangeEnd || isRangeMiddle) &&
    !(isRangeStart && isRangeEnd);

  return (
    <td
      {...(tdProps as React.TdHTMLAttributes<HTMLTableCellElement>)}
      className={cn(
        "relative flex-1 h-11 p-0 text-center group/day select-none",
        // Weekend colors (only when not selected and not disabled)
        !isSelected && !isDisabled && dayOfWeek === 0 && "[&_button]:text-red-500!",
        !isSelected && !isDisabled && dayOfWeek === 6 && "[&_button]:text-blue-500!",
        className,
      )}
    >
      {/* Gray range band — half-width for start/end, full for middle */}
      {showBand && (
        <div
          className={cn(
            "absolute top-1/2 -translate-y-1/2 h-9 bg-gray-100",
            isRangeStart && !isRangeEnd && "right-0 left-1/2",
            isRangeEnd && !isRangeStart && "left-0 right-1/2",
            isRangeMiddle && !isRangeStart && !isRangeEnd && "left-0 right-0",
          )}
        />
      )}
      {/* Button container — centered, above band */}
      <div className="relative flex items-center justify-center h-full z-10">
        {children}
      </div>
    </td>
  );
}

const calendarClassNames = {
  root: "w-full",
  months: "flex flex-col gap-4 overflow-hidden",
  nav: "hidden",
  month_caption: "flex items-center justify-center h-10",
  caption_label: "select-none text-base font-bold text-gray-900",
  weekdays: "flex rdp-weekdays",
  week: "flex w-full mt-1 rdp-week",
  // Day cell: handled by BookingDay component, keep minimal
  day: "",
  // Range: no td background — handled by BookingDay inner div
  range_start:
    "[&_button]:bg-gray-900! [&_button]:text-white! [&_button]:rounded-lg [&_button]:hover:bg-gray-900 [&_button]:hover:text-white",
  range_end:
    "[&_button]:bg-gray-900! [&_button]:text-white! [&_button]:rounded-lg [&_button]:hover:bg-gray-900 [&_button]:hover:text-white",
  range_middle:
    "[&_button]:bg-transparent! [&_button]:text-gray-900! [&_button]:hover:bg-transparent",
  // Today: ring outline only
  today: cn(
    "[&_button]:ring-1 [&_button]:ring-gray-300 [&_button]:rounded-lg",
    "[&_button]:data-[range-start=true]:ring-0 [&_button]:data-[range-end=true]:ring-0",
  ),
  // Day button: override CalendarDayButton hardcoded styles
  day_button: "text-base! font-medium! w-9! h-9! min-w-0! mx-auto",
  // Disabled dates
  disabled: "text-gray-300! opacity-100!",
};

export function BookingDatePicker({
  open,
  onOpenChange,
  mode,
  pickupDate,
  returnDate,
  onApply,
  onReset,
  disabledDate,
  locale,
  variant = "drawer",
  standalone = false,
  quickPicks = false,
}: BookingDatePickerProps) {
  const t = useTranslations("product.booking");

  const [tempRange, setTempRange] = useState<DateRange | undefined>(() => {
    if (mode === "daily" && pickupDate && returnDate) {
      return { from: pickupDate, to: returnDate };
    }
    return pickupDate ? { from: pickupDate } : undefined;
  });

  // Radix Dialog/Drawer는 프로그램적으로 open=true가 되어도 onOpenChange를 호출하지 않는다.
  // 따라서 handleOpenChange 내부 동기화만으로는 부족하고, open 전환 또는 pickup/return
  // 외부 변경 시 tempRange를 props에서 다시 맞춰줘야 한다.
  useEffect(() => {
    if (!open) return;
    if (mode === "daily" && pickupDate && returnDate) {
      setTempRange({ from: pickupDate, to: returnDate });
    } else if (pickupDate) {
      setTempRange({ from: pickupDate });
    } else {
      setTempRange(undefined);
    }
  }, [open, mode, pickupDate, returnDate]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) {
        if (mode === "daily") {
          if (pickupDate && returnDate) {
            setTempRange({ from: pickupDate, to: returnDate });
          } else if (pickupDate) {
            setTempRange({ from: pickupDate });
          } else {
            setTempRange(undefined);
          }
        } else {
          setTempRange(pickupDate ? { from: pickupDate } : undefined);
        }
      }
      onOpenChange(nextOpen);
    },
    [pickupDate, returnDate, mode, onOpenChange],
  );

  const handleApply = useCallback(() => {
    if (mode === "daily" && tempRange?.from && tempRange?.to) {
      onApply(tempRange.from, tempRange.to);
    } else if (mode === "timeslot" && tempRange?.from) {
      onApply(tempRange.from);
    }
    onOpenChange(false);
  }, [tempRange, mode, onApply, onOpenChange]);

  const handleReset = useCallback(() => {
    setTempRange(undefined);
  }, []);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // 과거 날짜 비활성화 + 외부에서 넘긴 disabledDate 병합
  // react-day-picker v9는 { before: Date } matcher 또는 matcher 배열을 받는다
  const combinedDisabled = disabledDate
    ? [{ before: today }, disabledDate]
    : { before: today };

  const tempDays =
    tempRange?.from && tempRange?.to
      ? Math.round(
          (tempRange.to.getTime() - tempRange.from.getTime()) /
            (1000 * 60 * 60 * 24),
        ) + 1
      : 0;

  const canApply =
    mode === "daily" ? !!tempRange?.from && !!tempRange?.to : !!tempRange?.from;

  const handleTimeslotSelect = useCallback((date: Date | undefined) => {
    if (date) {
      setTempRange({ from: date });
    }
  }, []);

  // Format date as "MM.DD(요일)" e.g. "03.22(일)"
  const formatDateWithDay = (date: Date) => {
    const mmdd = format(date, "MM.dd");
    const dayName = format(date, "EEE", { locale });
    return `${mmdd}(${dayName})`;
  };

  // Format month caption as "2026.03"
  const formatCaption = (date: Date) => {
    return format(date, "yyyy.MM");
  };

  const summaryBar = (
    <div className="px-6 my-2 flex items-center gap-2 bg-white py-2.5 border-b border-gray-200">
      <CalendarIcon className="h-5 w-5 shrink-0 text-gray-800" />
      {mode === "daily" ? (
        tempRange?.from && tempRange?.to ? (
          <span className="font-bold text-gray-900">
            {formatDateWithDay(tempRange.from)}~
            {formatDateWithDay(tempRange.to)} ·{" "}
            {t("days", { count: tempDays })}
          </span>
        ) : tempRange?.from ? (
          <span className="text-sm text-gray-500">
            {formatDateWithDay(tempRange.from)} ~{" "}
            {t("selectReturnDate")}
          </span>
        ) : (
          <span className="text-sm text-gray-400">
            {t("dateDrawerTitle")}
          </span>
        )
      ) : tempRange?.from ? (
        <span className="text-sm font-medium text-gray-900">
          {formatDateWithDay(tempRange.from)}
        </span>
      ) : (
        <span className="text-sm text-gray-400">
          {t("dateDrawerTitle")}
        </span>
      )}
    </div>
  );

  // 바로가기 기간 — 오늘/내일은 하루, 이번 주말은 금~일(이미 주말이면 오늘~일요일).
  const quickRanges = (() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const addDays = (d: Date, n: number) => {
      const x = new Date(d);
      x.setDate(x.getDate() + n);
      return x;
    };
    const dow = today.getDay(); // 0=일 … 6=토
    const sunday = addDays(today, (7 - dow) % 7);
    const friday = addDays(sunday, -2);
    const weekendStart = friday < today ? today : friday;
    return [
      { key: "quickToday", from: today, to: today },
      { key: "quickTomorrow", from: addDays(today, 1), to: addDays(today, 1) },
      { key: "quickWeekend", from: weekendStart, to: sunday },
    ];
  })();
  const sameDay = (a?: Date, b?: Date) =>
    !!a && !!b && a.toDateString() === b.toDateString();

  const quickPickRow =
    quickPicks && mode === "daily" ? (
      // 이번 주말 칸만 넓게(1 : 1 : 1.4) — "10.09(금)~10.11(일)" 이 두 줄로 깨지지 않게(10-06).
      <div className="mx-3 mt-1 grid grid-cols-[1fr_1fr_1.4fr] gap-2">
        {quickRanges.map((range) => {
          const on =
            sameDay(tempRange?.from, range.from) &&
            sameDay(tempRange?.to, range.to);
          return (
            <button
              key={range.key}
              type="button"
              aria-pressed={on}
              onClick={() => setTempRange({ from: range.from, to: range.to })}
              className={cn(
                "rounded-2xl px-3 py-3 text-left transition",
                on
                  ? "border-2 border-gray-900"
                  : "border border-gray-300 hover:bg-gray-50",
              )}
            >
              <span className="block text-[15px] font-semibold text-gray-900">
                {t(range.key)}
              </span>
              <span className="mt-0.5 block text-xs text-gray-500">
                {sameDay(range.from, range.to)
                  ? formatDateWithDay(range.from)
                  : `${formatDateWithDay(range.from)}~${formatDateWithDay(range.to)}`}
              </span>
            </button>
          );
        })}
      </div>
    ) : null;

  const calendarContent = (
    <div className="flex-1 overflow-auto min-h-0 px-3">
      {mode === "daily" ? (
        <Calendar
          mode="range"
          selected={tempRange}
          onSelect={setTempRange}
          numberOfMonths={3}
          disabled={combinedDisabled}
          locale={locale}
          showOutsideDays={false}
          className={calendarClassName}
          classNames={calendarClassNames}
          formatters={{ formatCaption }}
          components={{ Day: BookingDay as never }}
        />
      ) : (
        <Calendar
          mode="single"
          selected={tempRange?.from}
          onSelect={handleTimeslotSelect}
          numberOfMonths={3}
          disabled={combinedDisabled}
          locale={locale}
          showOutsideDays={false}
          className={calendarClassName}
          classNames={{
            ...calendarClassNames,
            range_start: "",
            range_end: "",
            range_middle: "",
          }}
          formatters={{ formatCaption }}
          components={{ Day: BookingDay as never }}
        />
      )}
    </div>
  );

  const handleResetClick = useCallback(() => {
    setTempRange(undefined);
    onReset?.();
    onOpenChange(false);
  }, [onReset, onOpenChange]);

  const renderApplyButton = (isDrawer: boolean) => (
    <div className={cn("flex items-center gap-2 px-3 pt-3 pb-3", isDrawer && "pb-3")}>
      {onReset && (
        <Button
          size="lg"
          variant="outline"
          onClick={handleResetClick}
          className="h-12 px-5 border-gray-200 text-gray-600 hover:bg-gray-50 rounded-xl"
        >
          {t("dateReset")}
        </Button>
      )}
      <Button
        size="lg"
        onClick={handleApply}
        disabled={!canApply}
        className="flex-1 h-12 bg-orange-500 hover:bg-orange-600 text-base text-white rounded-xl"
      >
        {t("dateApply")}
      </Button>
    </div>
  );

  if (variant === "dialog") {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-6 pb-2">
            <DialogTitle className="text-base font-bold">
              {t("dateDrawerTitle")}
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col min-h-0 flex-1">
            {quickPickRow}
            <div className="flex flex-col min-h-0 flex-1 mx-3 my-2 border border-gray-800 rounded-2xl overflow-hidden">
              {summaryBar}
              {calendarContent}
            </div>
            {renderApplyButton(false)}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const drawerInner = (
    <DrawerContent className="max-h-[92vh]">
      <DrawerHeader className="pb-2">
        <DrawerTitle className="text-base font-bold">
          {t("dateDrawerTitle")}
        </DrawerTitle>
      </DrawerHeader>
      <div className="flex flex-col min-h-0 flex-1">
        {quickPickRow}
        <div className="flex flex-col min-h-0 flex-1 mx-3 my-2 border border-gray-800 rounded-2xl overflow-hidden">
          {summaryBar}
          {calendarContent}
        </div>
        {renderApplyButton(true)}
      </div>
    </DrawerContent>
  );

  if (standalone) {
    return (
      <Drawer open={open} onOpenChange={handleOpenChange} noBodyStyles>
        {drawerInner}
      </Drawer>
    );
  }

  return (
    <DrawerNestedRoot open={open} onOpenChange={handleOpenChange}>
      {drawerInner}
    </DrawerNestedRoot>
  );
}
